<script setup>
/**
 * The whole page while this device holds an encrypted org's file but not its
 * key (`state.sync.gate`, set by main.js's bootVault): nothing of the ledger
 * can be shown until it has one. An owner may type the passphrase or the
 * recovery key; anyone may be approved by a device that has the key — six
 * digits to compare, or a QR code for it to scan. Once the key is here and the
 * ledger in, sync says `synced` and the app takes the page.
 */
import { computed, reactive, ref, watch } from 'vue'
import Icon from './ui/Icon.vue'
import { state } from '../stores/state'
import { errorText, t } from '../stores/i18n'
import * as cloud from '../stores/cloud'
import * as sync from '../stores/sync'
import { qrSvg } from '../camera'

const form = reactive({ secret: '', recovery: false })
const busy = ref(false)
const error = ref('')
const svg = ref('')
const link = ref('')

const org = computed(() => state.cloud.org ?? state.sync.org)
const role = computed(() => state.sync.role ?? state.cloud.org?.role ?? null)
const account = computed(() => state.cloud.account)
const others = computed(() => (account.value?.orgs ?? []).filter((o) => o.id !== org.value?.id))

async function unlock() {
  busy.value = true
  error.value = ''
  try {
    await sync.unlock(form.recovery ? { recovery: form.secret } : { passphrase: form.secret })
    form.secret = ''
  } catch (err) {
    error.value =
      err?.code === 'wrong-passphrase'
        ? t('sync.wrongPassphrase')
        : err?.code === 'wrong-recovery-key' || err?.code === 'bad-recovery-key'
          ? t('sync.wrongRecovery')
          : errorText(err)
  } finally {
    busy.value = false
  }
}

async function showCode() {
  if (svg.value) return (svg.value = link.value = '')
  link.value = await sync.deviceLink()
  svg.value = await qrSvg(link.value)
}

// Approved while the code was on screen: nothing left to show.
watch(
  () => state.sync.gate,
  (gated) => !gated && (svg.value = ''),
)

function openOther(id) {
  sync.setVaultOrg(null)
  cloud.setOrgId(id)
  location.reload()
}
function signInAgain() {
  // Boot then asks who is signed in first, and comes back to this org.
  sync.setVaultOrg(null)
  location.reload()
}
function onItsOwn() {
  cloud.setMode('local')
  location.reload()
}
async function signOut() {
  await cloud.signOut().catch(() => {})
  sync.setVaultOrg(null)
  location.reload()
}

const STATUS = ['connecting', 'offline', 'signed-out', 'error', 'gone', 'schema-ahead']
</script>

<template>
  <div class="flex min-h-full items-start justify-center px-4 pt-[max(3rem,env(safe-area-inset-top))] pb-10" data-sync-gate>
    <div class="grid w-full max-w-md gap-6">
      <header class="grid justify-items-center gap-2 text-center">
        <img src="/mark.png" alt="" class="h-12 w-12" />
        <p class="flex items-center gap-1.5 text-[12.5px] text-muted"><Icon name="lock" :size="14" class="text-accent" />{{ t('gate.kind') }}</p>
        <h1 class="text-[22px] font-semibold" data-gate-org>{{ org?.name }}</h1>
        <p class="text-[13.5px] text-muted">{{ t('gate.lead') }}</p>
      </header>

      <p v-if="STATUS.includes(state.sync.status)" class="rounded-xl bg-raised px-3 py-2 text-center text-[13px]" :class="state.sync.status === 'connecting' ? 'text-muted' : 'text-warn'" data-gate-status>
        {{ t(`sync.status.${state.sync.status}`) }}
      </p>
      <button v-if="state.sync.status === 'signed-out'" class="btn-primary justify-center" @click="signInAgain">{{ t('gate.signIn') }}</button>

      <!-- An owner can open it here. -->
      <form v-if="role === 'owner' && state.sync.status !== 'gone'" class="grid gap-2 rounded-2xl border border-line-soft p-4" data-gate-unlock @submit.prevent="unlock">
        <p class="text-[13px] font-semibold">{{ t('gate.ownerTitle') }}</p>
        <input
          v-model="form.secret"
          class="input"
          :type="form.recovery ? 'text' : 'password'"
          autocomplete="current-password"
          :placeholder="form.recovery ? t('sync.recoveryPlaceholder') : t('sync.passphrase')"
          :aria-label="form.recovery ? t('sync.recoveryPlaceholder') : t('sync.passphrase')"
          data-gate-secret
        />
        <button type="button" class="btn-ghost justify-self-start !text-[12.5px]" @click="form.recovery = !form.recovery">
          {{ form.recovery ? t('sync.usePassphrase') : t('sync.useRecovery') }}
        </button>
        <button class="btn-primary justify-center" :disabled="!form.secret.trim() || busy" data-primary>
          <Icon name="key" :size="15" /> {{ busy ? t('gate.opening') : t('gate.open') }}
        </button>
      </form>

      <!-- Anyone can be approved by a device that has the key. -->
      <section v-if="state.sync.code" class="grid justify-items-center gap-3 rounded-2xl border border-line-soft p-4 text-center" data-gate-approve>
        <p class="text-[13px]">{{ role === 'owner' ? t('gate.orApprove') : t('sync.lockedLead') }}</p>
        <p class="font-mono text-[28px] tracking-widest" data-sync-code>{{ state.sync.code }}</p>
        <button class="btn-soft" data-sync-show-device @click="showCode">
          <Icon name="qr" :size="15" /> {{ svg ? t('sync.hideCode') : t('sync.showDeviceCode') }}
        </button>
        <div v-if="svg" class="grid justify-items-center gap-2" data-sync-qr :data-link="link">
          <div class="w-48 rounded-lg bg-white p-2" v-html="svg" />
          <p class="text-[12.5px] text-muted">{{ t('sync.qrDeviceLead') }}</p>
        </div>
      </section>

      <p v-if="error" class="text-center text-[12.5px] text-warn" role="alert">{{ error }}</p>

      <footer class="grid gap-2 border-t border-line-soft pt-4 text-[12.5px] text-faint">
        <p v-if="account">{{ t('sync.signedInAs', { email: account.user.email }) }}</p>
        <select v-if="others.length" class="input" :aria-label="t('gate.openAnother')" @change="openOther($event.target.value)">
          <option value="" selected disabled>{{ t('gate.openAnother') }}</option>
          <option v-for="o in others" :key="o.id" :value="o.id">{{ o.name }}</option>
        </select>
        <div class="flex flex-wrap gap-2">
          <button class="btn-ghost !text-[12.5px]" @click="onItsOwn">{{ t('gate.onItsOwn') }}</button>
          <button v-if="account" class="btn-ghost !text-[12.5px]" @click="signOut">{{ t('gate.signOut') }}</button>
        </div>
      </footer>
    </div>
  </div>
</template>
